import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { register, handleSubmit, watch, formState: { errors }, setError, clearErrors } = useForm();
  const password = watch('password');

  const onSubmit = async (data) => {
    setLoading(true);
    setAuthError(null);
    clearErrors();
    
    const result = await registerUser(data);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setAuthError(result.error);
      // Si l'erreur concerne l'email déjà utilisé
      if (result.error.includes('email') || result.error.includes('déjà')) {
        setError('email', { type: 'manual', message: result.error });
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Inscription</h1>
          <p style={styles.subtitle}>Créez votre compte</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          {authError && (
            <div style={styles.alertError}>
              <span style={styles.alertIcon}>⚠️</span>
              <span>{authError}</span>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Nom complet</label>
            <input
              type="text"
              {...register('fullName', {
                required: 'Le nom complet est requis',
              })}
              style={{
                ...styles.input,
                ...(errors.fullName ? styles.inputError : {}),
              }}
              placeholder="John Doe"
            />
            {errors.fullName && (
              <span style={styles.error}>{errors.fullName.message}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              {...register('email', {
                required: 'L\'email est requis',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Email invalide',
                },
                onChange: () => {
                  // Effacer l'erreur d'authentification quand l'utilisateur tape
                  if (authError) {
                    setAuthError(null);
                    clearErrors('email');
                  }
                },
              })}
              style={{
                ...styles.input,
                ...(errors.email ? styles.inputError : {}),
              }}
              placeholder="votre@email.com"
            />
            {errors.email && (
              <span style={styles.error}>{errors.email.message}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Mot de passe</label>
            <input
              type="password"
              {...register('password', {
                required: 'Le mot de passe est requis',
                minLength: {
                  value: 8,
                  message: 'Le mot de passe doit contenir au moins 8 caractères',
                },
              })}
              style={{
                ...styles.input,
                ...(errors.password ? styles.inputError : {}),
              }}
              placeholder="••••••••"
            />
            {errors.password && (
              <span style={styles.error}>{errors.password.message}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirmer le mot de passe</label>
            <input
              type="password"
              {...register('confirmPassword', {
                required: 'La confirmation est requise',
                validate: (value) =>
                  value === password || 'Les mots de passe ne correspondent pas',
              })}
              style={{
                ...styles.input,
                ...(errors.confirmPassword ? styles.inputError : {}),
              }}
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <span style={styles.error}>{errors.confirmPassword.message}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={styles.button}
          >
            {loading ? 'Inscription...' : 'S\'inscrire'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Déjà un compte ?{' '}
            <Link to="/login" style={styles.link}>
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--text)',
  },
  input: {
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  inputError: {
    borderColor: 'var(--danger)',
  },
  error: {
    color: 'var(--danger)',
    fontSize: '0.75rem',
  },
  button: {
    padding: '0.75rem',
    background: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  footer: {
    marginTop: '1.5rem',
    textAlign: 'center',
  },
  footerText: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
  },
  link: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontWeight: 500,
  },
  alertError: {
    background: '#fee2e2',
    border: '1px solid #fca5a5',
    borderRadius: '0.5rem',
    padding: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#991b1b',
    fontSize: '0.875rem',
  },
  alertIcon: {
    fontSize: '1rem',
  },
};

export default Register;








